import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, ButtonGroup, Form, InputGroup, Modal } from 'react-bootstrap';
import moment from 'moment';
import api from '../Components/ApiClient';
import { useParams } from 'react-router-dom';
import { connectRaceHub } from '../services/raceHub';
import ConfirmModal from '../Components/ConfirmModal';

interface Checkpoint {
	id: number;
	timestamp: string;
	raceId: string;
	competitorId?: string;
	transponderId: string;
	source: string;
	deleted: boolean;
}

interface Competitor {
	id: string;
	name: string;
	number: number;
	group: string;
	transponder: string;
	dnf: boolean;
}

const CheckpointsPage: React.FC = () => {
	const { raceid } = useParams<{ raceid: string }>();

	const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
	const [competitors, setCompetitors] = useState<Competitor[]>([]);
	const [competitorMap, setCompetitorMap] = useState<Record<string, Competitor>>({});
	const [filter, setFilter] = useState('');

	// Manual insert
	const [showManualInsert, setShowManualInsert] = useState(false);
	const [manualCompetitorNumber, setManualCompetitorNumber] = useState('');
	const [manualTime, setManualTime] = useState('');

	// Confirm dialog
	const [confirmDialog, setConfirmDialog] = useState<{ title: string; message: string; variant?: string; onConfirm: () => void } | null>(null);

	// Merge
	const [showMerge, setShowMerge] = useState(false);
	const [mergeSource, setMergeSource] = useState('');
	const [mergeTarget, setMergeTarget] = useState('');

	// Build competitor lookup map
	useEffect(() => {
		const map: Record<string, Competitor> = {};
		for (const c of competitors) {
			map[c.id] = c;
		}
		setCompetitorMap(map);
	}, [competitors]);

	const loadCheckpoints = useCallback(async () => {
		if (!raceid) return;
		try {
			const response: Checkpoint[] = await api.request(`checkpoints?raceid=${raceid}`);
			// The api client swallows failures and resolves undefined; keep what we have.
			if (!response) return;
			setCheckpoints(response.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
		} catch (error) {
			console.error('Error loading checkpoints', error);
		}
	}, [raceid]);

	const loadCompetitors = useCallback(async () => {
		if (!raceid) return;
		try {
			const response: Competitor[] = await api.request(`competitors/inrace?raceid=${raceid}`);
			if (!response) return;
			setCompetitors(response);
		} catch (error) {
			console.error('Error loading competitors', error);
		}
	}, [raceid]);

	// Initial data load + SignalR connection
	useEffect(() => {
		loadCheckpoints();
		loadCompetitors();

		if (!raceid) return;

		return connectRaceHub({
			raceId: raceid,

			onCheckpointAdded: (checkpoint: Checkpoint, competitor: Competitor | null) => {
				setCheckpoints(prev => [checkpoint, ...prev]);
				if (competitor && competitor.id) {
					setCompetitors(prev => {
						const exists = prev.some(c => c.id === competitor.id);
						if (!exists) return [...prev, competitor];
						return prev;
					});
				}
			},

			// Anything broadcast while the tab was suspended never arrived, so pull
			// the full list again rather than trusting the incremental state.
			onResync: () => {
				loadCheckpoints();
				loadCompetitors();
			}
		});
	}, [raceid, loadCheckpoints, loadCompetitors]);

	// Filtered checkpoints
	const filteredCheckpoints = useMemo(() => {
		if (!filter.trim()) return checkpoints;
		const f = filter.toLowerCase();
		return checkpoints.filter(cp => {
			const comp = cp.competitorId ? competitorMap[cp.competitorId] : null;
			if (comp) {
				if (comp.name?.toLowerCase().includes(f)) return true;
				if (comp.number?.toString().includes(f)) return true;
				if (comp.group?.toLowerCase().includes(f)) return true;
			}
			if (cp.transponderId?.toLowerCase().includes(f)) return true;
			return false;
		});
	}, [checkpoints, filter, competitorMap]);

	// Soft delete
	const deleteCheckpoint = useCallback(async (cp: Checkpoint) => {
		try {
			await api.delete(`checkpoints/delete/${cp.id}`);
			setCheckpoints(prev => prev.map(c => c.id === cp.id ? { ...c, deleted: true } : c));
		} catch (error) {
			console.error('Error deleting checkpoint', error);
		}
	}, []);

	// Undelete
	const undeleteCheckpoint = useCallback(async (cp: Checkpoint) => {
		try {
			await api.put(`checkpoints/undelete/${cp.id}`, null);
			setCheckpoints(prev => prev.map(c => c.id === cp.id ? { ...c, deleted: false } : c));
		} catch (error) {
			console.error('Error undeleting checkpoint', error);
		}
	}, []);

	// Reset all — double confirmation via modal
	const resetAllCheckpoints = useCallback(() => {
		if (!raceid) return;
		setConfirmDialog({
			title: 'Reset All Checkpoints',
			message: 'This will permanently delete ALL checkpoints for this race. Are you sure?',
			variant: 'danger',
			onConfirm: () => {
				setConfirmDialog({
					title: 'Are you ABSOLUTELY sure?',
					message: 'This cannot be undone. All checkpoint data for this race will be permanently lost.',
					variant: 'danger',
					onConfirm: async () => {
						setConfirmDialog(null);
						try {
							await api.post(`checkpoints/reset/${raceid}`, null);
							setCheckpoints([]);
						} catch (error) {
							console.error('Error resetting checkpoints', error);
						}
					}
				});
			}
		});
	}, [raceid]);

	// Set start time
	const setStartTime = useCallback(() => {
		if (!raceid) return;
		setConfirmDialog({
			title: 'Set Start Time',
			message: `Set race start time to NOW (${moment().format('HH:mm:ss')})?`,
			variant: 'success',
			onConfirm: async () => {
				setConfirmDialog(null);
				const now = new Date().toISOString();
				try {
					await api.post(`checkpoints/setstart/${raceid}`, { startTime: now });
				} catch (error) {
					console.error('Error setting start time', error);
				}
			}
		});
	}, [raceid]);

	// Manual insert
	const handleManualInsert = useCallback(async () => {
		if (!raceid || !manualCompetitorNumber) return;

		const comp = competitors.find(c => c.number.toString() === manualCompetitorNumber);
		if (!comp) return;

		const timestamp = manualTime
			? new Date(manualTime).toISOString()
			: new Date().toISOString();

		try {
			const transponderId = comp.transponder || manualCompetitorNumber;
			await api.post(`checkpoints/add/${raceid}`, { timestamp, transponderId });
			setManualCompetitorNumber('');
			setManualTime('');
			// SignalR will push the new checkpoint, but also reload to be sure
			await loadCheckpoints();
		} catch (error) {
			console.error('Error inserting manual checkpoint', error);
		}
	}, [raceid, manualCompetitorNumber, manualTime, competitors, loadCheckpoints]);

	// Merge competitors
	const handleMerge = useCallback(() => {
		if (!raceid || !mergeSource || !mergeTarget || mergeSource === mergeTarget) return;
		const sourceName = competitorMap[mergeSource]?.name || mergeSource;
		const targetName = competitorMap[mergeTarget]?.name || mergeTarget;
		setConfirmDialog({
			title: 'Confirm Merge',
			message: `Merge all checkpoints from "${sourceName}" into "${targetName}"?`,
			variant: 'warning',
			onConfirm: async () => {
				setConfirmDialog(null);
				try {
					await api.post(`checkpoints/merge`, { raceId: raceid, sourceCompetitorId: mergeSource, targetCompetitorId: mergeTarget });
					setShowMerge(false);
					setMergeSource('');
					setMergeTarget('');
					await loadCheckpoints();
				} catch (error) {
					console.error('Error merging competitors', error);
				}
			}
		});
	}, [raceid, mergeSource, mergeTarget, competitorMap, loadCheckpoints]);

	return (
		<div className="container-fluid mt-3">
			<div className="d-flex justify-content-between align-items-center mb-3">
				<h2>Checkpoints</h2>
				<div>
					<span className="text-muted">{checkpoints.length} total, {filteredCheckpoints.length} shown</span>
				</div>
			</div>

			{/* Action buttons */}
			<ButtonGroup className="mb-3 me-2">
				<Button variant="success" onClick={setStartTime}>⏱ Set Start Time</Button>
				<Button variant="primary" onClick={() => setShowManualInsert(true)}>+ Manual Insert</Button>
				<Button variant="info" onClick={() => setShowMerge(true)}>Merge Competitors</Button>
				<Button variant="danger" onClick={resetAllCheckpoints}>Reset All</Button>
			</ButtonGroup>

			{/* Filter */}
			<InputGroup className="mb-3" style={{ maxWidth: 400 }}>
				<InputGroup.Text>Filter</InputGroup.Text>
				<Form.Control
					type="text"
					placeholder="Name, number, or group..."
					value={filter}
					onChange={(e) => setFilter(e.target.value)}
				/>
				{filter && (
					<Button variant="outline-secondary" onClick={() => setFilter('')}>&times;</Button>
				)}
			</InputGroup>



			{/* Checkpoint table */}
			<table className="table table-striped table-sm">
				<thead className="table-dark">
					<tr>
						<th>Time</th>
						<th>#</th>
						<th>Name</th>
						<th>Group</th>
						<th>Transponder</th>
						<th style={{ width: 120 }}>Actions</th>
					</tr>
				</thead>
				<tbody>
					{filteredCheckpoints.map(cp => {
						const comp = cp.competitorId ? competitorMap[cp.competitorId] : null;
						return (
							<tr key={cp.id} className={cp.deleted ? 'table-danger text-decoration-line-through' : ''}>
								<td>{moment(cp.timestamp).format('HH:mm:ss.SSS')}</td>
								<td>{comp?.number ?? ''}</td>
								<td>{comp?.name ?? '—'}</td>
								<td>{comp?.group ?? ''}</td>
								<td>{cp.transponderId}</td>
								<td>
									{cp.deleted ? (
										<Button variant="success" size="sm" onClick={() => undeleteCheckpoint(cp)}>Undelete</Button>
									) : (
										<Button variant="outline-danger" size="sm" onClick={() => deleteCheckpoint(cp)}>Delete</Button>
									)}
								</td>
							</tr>
						);
					})}
					{filteredCheckpoints.length === 0 && (
						<tr><td colSpan={6} className="text-center text-muted py-3">No checkpoints</td></tr>
					)}
				</tbody>
			</table>

			{/* Manual Insert modal */}
			<Modal show={showManualInsert} onHide={() => setShowManualInsert(false)}>
				<Modal.Header closeButton>
					<Modal.Title>Manual Insert</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Form.Group className="mb-3">
						<Form.Label>Competitor</Form.Label>
						<Form.Select
							value={manualCompetitorNumber}
							onChange={(e) => setManualCompetitorNumber(e.target.value)}
						>
							<option value="">-- Select --</option>
							{competitors
								.slice()
								.sort((a, b) => a.number - b.number)
								.map(c => (
									<option key={c.id} value={c.number.toString()}>
										#{c.number} — {c.name}
									</option>
								))}
						</Form.Select>
					</Form.Group>
					<Form.Group className="mb-3">
						<Form.Label>Time (leave empty for now)</Form.Label>
						<Form.Control
							type="datetime-local"
							step="0.001"
							value={manualTime}
							onChange={(e) => setManualTime(e.target.value)}
						/>
					</Form.Group>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setShowManualInsert(false)}>Cancel</Button>
					<Button variant="primary" onClick={() => { handleManualInsert(); setShowManualInsert(false); }} disabled={!manualCompetitorNumber}>
						Insert
					</Button>
				</Modal.Footer>
			</Modal>

			{/* Merge modal */}
			<Modal show={showMerge} onHide={() => setShowMerge(false)}>
				<Modal.Header closeButton>
					<Modal.Title>Merge Competitors</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>Move all checkpoints from one competitor to another.</p>
					<Form.Group className="mb-3">
						<Form.Label>Source (will lose checkpoints)</Form.Label>
						<Form.Select value={mergeSource} onChange={(e) => setMergeSource(e.target.value)}>
							<option value="">-- Select --</option>
							{competitors.map(c => (
								<option key={c.id} value={c.id}>#{c.number} — {c.name}</option>
							))}
						</Form.Select>
					</Form.Group>
					<Form.Group className="mb-3">
						<Form.Label>Target (will receive checkpoints)</Form.Label>
						<Form.Select value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)}>
							<option value="">-- Select --</option>
							{competitors.map(c => (
								<option key={c.id} value={c.id}>#{c.number} — {c.name}</option>
							))}
						</Form.Select>
					</Form.Group>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => setShowMerge(false)}>Cancel</Button>
					<Button variant="warning" onClick={handleMerge} disabled={!mergeSource || !mergeTarget || mergeSource === mergeTarget}>
						Merge
					</Button>
				</Modal.Footer>
			</Modal>
					{/* Confirm dialog */}
					<ConfirmModal
						show={!!confirmDialog}
						title={confirmDialog?.title || ''}
						message={confirmDialog?.message || ''}
						variant={confirmDialog?.variant}
						onConfirm={() => confirmDialog?.onConfirm()}
						onCancel={() => setConfirmDialog(null)}
					/>
				</div>
			);
		};

		export default CheckpointsPage;
